import React, { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import FormField from '../../FormField';
import { GENERATE_POST_DESCRIPTION } from '../../../graphql/GENERATE_POST_DESCRIPTION';

interface DescriptionFormProps {
  onDescriptionGenerated: (description: string) => void;
  formValues: any; // Form values from the parent component
}

const MOBILE_BREAKPOINT = 768;
const MODAL_RENDER_DELAY = 100;

const GenerateDescriptionForm: React.FC<DescriptionFormProps> = ({
  onDescriptionGenerated,
  formValues
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const modalScrollRef = useRef<HTMLDivElement>(null);

  const formSchema = Yup.object().shape({
    userInput: Yup.string()
      .required('This field is required')
      .min(3, 'At least 3 characters')
  });

  const initialValues = {
    userInput: ''
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };

  useEffect(() => {
    if (!modalIsOpen) return;

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    const scrollTimeout = setTimeout(() => {
      const modal = modalScrollRef.current;
      if (!modal) return;

      if (isMobile) {
        const modalRect = modal.getBoundingClientRect();
        const scrollPosition = modalRect.top + window.pageYOffset -
          (window.innerHeight - modalRect.height) / 2;

        window.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        modal.scrollTop = 0;
      }
    }, MODAL_RENDER_DELAY);

    return () => clearTimeout(scrollTimeout);
  }, [modalIsOpen]);

  const prepareGenerationData = (userInput: string) => {
    // Extract useful information from formValues
    const {
      title = '',
      genres = [],
      type = '',
      collaborations = [],
      fundingStatus = '',
      locations = []
    } = formValues;

    // Format collaborations info
    const collaborationsText = collaborations
      .filter((collab) => collab.requiredSkills?.length > 0)
      .map((collab) => {
        const skills = collab.requiredSkills?.join(', ') || '';
        const compensation = collab.compensation || '';
        const requirements = collab.additionalRequirements || '';
        return `Need: ${skills}${requirements ? ` - ${requirements}` : ''}${
          compensation ? ` (${compensation})` : ''
        }`;
      })
      .join('; ');

    // Format genres
    const genresText = genres.join(', ');

    // Format locations
    const locationsText = locations.join(', ');

    // Create structured data to send to backend
    return {
      userInput,
      projectContext: {
        title,
        type,
        genres: genresText,
        locations: locationsText,
        collaborations: collaborationsText,
        fundingStatus
      }
    };
  };

  const [generateDescription, { loading }] = useMutation(
    GENERATE_POST_DESCRIPTION,
    {
      onCompleted: (res) => {
        const description = res?.generatePostDescription?.description;
        if (description && typeof description === 'string') {
          onDescriptionGenerated(description);
          handleCloseModal();
          toast.success('Description generated successfully!');
        }
      },
      onError: () => toast.error('Failed to generate description')
    }
  );

  return (
    <>
      <div className="level level-right">
        <button
          type="button"
          className="button generate-description-button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary"
          onClick={() => setModalIsOpen(true)}
        >
          Generate With Matchhat
        </button>
      </div>
      <div className={`modal ${modalIsOpen ? 'is-active' : ''}`}>
        <div className="modal-background">
          <section className="container">
            <div className="columns section page-add-project-container">
              <div className="column">
                <div
                  ref={modalScrollRef}
                  style={{
                    maxHeight: '80vh',
                    overflowY: 'auto'
                  }}
                >
                  <div className="card page-add-project-card is-rounded is-dropshadowed">
                    <Formik
                      validationSchema={formSchema}
                      initialValues={initialValues}
                      onSubmit={async (values, { setSubmitting }) => {
                        await generateDescription({
                          variables: prepareGenerationData(values.userInput)
                        });
                        setSubmitting(false);
                      }}
                    >
                      {({
                        values,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting
                      }) => (
                        <>
                          <header className="card-header page-add-project-card-header is-bordered-bottom">
                            <div className="card-header-title is-block">
                              <div className="level">
                                <div className="level-left">
                                  <h1 className="has-text-weight-semibold is-light is-size-5">
                                    Generate Project Description
                                  </h1>
                                </div>
                              </div>
                            </div>
                          </header>
                          <div className="card-content page-add-project-card-content">
                            <div className="columns">
                              <div className="column is-one-quarter">
                                <h3 className="is-size-5 has-text-weight-bold">
                                  Describe your project idea
                                  <span className="has-text-danger">*</span>
                                </h3>
                              </div>
                              <div className="column is-three-quarters">
                                <div className="field">
                                  <div className="field-body">
                                    <div
                                      className="control"
                                      style={{ width: '100%' }}
                                    >
                                      <FormField
                                        multiline={true}
                                        autoResize={true}
                                        name="userInput"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Add a brief description or some keywords about your project (e.g., 'Short film about climate change' or 'Music video for indie rock band')"
                                        value={values.userInput}
                                        additionalInputClasses="has-background-white textarea"
                                        rows={6}
                                        style={{
                                          minHeight: '150px',
                                          width: '100%'
                                        }}
                                      />
                                      <p className="help">
                                        Provide some context about your project,
                                        and we'll generate a detailed
                                        description.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="page-add-project-controls is-bordered-top">
                            <div className="level container page-add-project-controls-container">
                              <div className="level-left page-add-project-controls-container-level" />
                              <div className="level-right page-add-project-controls-container-level">
                                <button
                                  type="button"
                                  className="button is-fullwidth-mobile is-borderless page-add-project-controls-button has-background-white-ter has-text-weight-semibold has-text-primary"
                                  disabled={isSubmitting}
                                  onClick={handleCloseModal}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className={`button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary ${
                                    loading || isSubmitting ? 'is-loading' : ''
                                  }`}
                                  disabled={loading || isSubmitting}
                                  onClick={() => handleSubmit()}
                                >
                                  Generate
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default GenerateDescriptionForm;
